import { z } from 'zod';
import {
  AnalysisBadgeSchema,
  AnalysisCostSummarySchema,
  AnalysisPostingTimePatternsSchema,
  AnalysisTweetsAnalyzedSchema,
  AnalysisUsageSummarySchema,
} from './shared/analysis.js';

export {
  AnalysisBadgeSchema as TimelineAnalysisBadgeSchema,
  AnalysisCostSummarySchema as TimelineAnalysisCostSummarySchema,
  AnalysisPercentageBreakdownSchema as TimelineAnalysisPercentageBreakdownSchema,
  AnalysisPostingTimePatternsSchema as TimelineAnalysisPostingTimePatternsSchema,
  AnalysisUsageSummarySchema as TimelineAnalysisUsageSummarySchema,
  AnalysisWeekdayWeekendSchema as TimelineAnalysisWeekdayWeekendSchema,
} from './shared/analysis.js';

export const TimelineTweetHandleSpanSchema = z.object({
  start: z.string(),
  end: z.string(),
});

// Drives the analysis summary widgets rendered in TimelineAnalysisCardInsightsView.
export const TimelineAnalysisActivityPatternsSchema = z.object({
  mostActiveTime: z.string().optional(),
  tweetFrequency: z.string().optional(),
  engagementRate: z.number().optional(),
});

export const TimelineAnalysisTweetsAnalyzedSchema = AnalysisTweetsAnalyzedSchema.extend({
  count: z.number(),
}).strip();

// Used by persistence + ingestion pipelines (e.g. src/app/api/timeline/lib/persistence.ts,
// src/lib/inngest/schemas.ts) to enforce a single shape for AI metadata before it reaches the UI.
export const TimelineAnalysisAiMetadataSchema = z.object({
  model: z.string().optional(),
  timestamp: z.string().optional(),
  generationTimeMs: z.number().optional(),
  tweetsAnalyzed: TimelineAnalysisTweetsAnalyzedSchema.optional(),
  usage: AnalysisUsageSummarySchema.optional(),
  cost: AnalysisCostSummarySchema.optional(),
});

const TimelineTopicFeaturedLinkCommentSchema = z
  .object({
    tweetHandle: z.string().min(1),
    comment: z.string().min(1),
    engagement: z
      .object({
        likes: z.number().int().min(0).optional(),
        quotes: z.number().int().min(0).optional(),
        replies: z.number().int().min(0).optional(),
      })
      .optional(),
  })
  .strip();

const TimelineTopicFeaturedLinkSchema = z
  .object({
    url: z.string().url(),
    title: z.string().optional(),
    summary: z.string().optional(),
    imageUrl: z.string().url().optional(),
    siteName: z.string().optional(),
    sharedBy: z.array(z.string().min(1)).max(10).optional(),
    topComments: z.array(TimelineTopicFeaturedLinkCommentSchema).max(5).optional(),
  })
  .strip();

export const TimelineTopicSchema = z.object({
  id: z.string(),
  title: z.string(),
  summary: z.string(),
  sentiment: z.enum(['positive', 'neutral', 'negative']).default('neutral'),
  tweetIds: z.array(z.string()),
  highlightTweetIds: z.array(z.string()).optional(),
  tweetHandleSpans: z.array(TimelineTweetHandleSpanSchema).optional(),
  keywords: z.array(z.string()).optional(),
  featuredLink: TimelineTopicFeaturedLinkSchema.optional(),
});

export const TimelineHighlightSchema = z.object({
  id: z.string(),
  title: z.string(),
  summary: z.string().optional(),
  tweetIds: z.array(z.string()),
  reason: z.string().optional(),
  category: z.string().optional(),
});

export const HighlightSectionSchema = TimelineHighlightSchema;

// Central contract consumed by UI, CLI, email exports, and webhook/persistence layers.
export const UITimelineAnalysisSchema = z.object({
  summary: z.string().optional(),
  topics: z.array(TimelineTopicSchema).default([]),
  highlights: z.array(TimelineHighlightSchema).optional(),
  estimatedCharacterCount: z.number().optional(),
  sentiment: z.enum(['positive', 'neutral', 'negative']).optional(),
  sentimentOverview: z.string().optional(),
  insights: z.array(z.string()).optional(),
  recommendations: z.array(z.string()).optional(),
  activityPatterns: TimelineAnalysisActivityPatternsSchema.optional(),
  postingTimePatterns: AnalysisPostingTimePatternsSchema.optional(),
  aiMetadata: TimelineAnalysisAiMetadataSchema.optional(),
  badges: z.array(AnalysisBadgeSchema).optional(),
  surprisingFacts: z.array(z.string()).optional(),
  listsSummary: z.string().optional(),
});

export type TimelineTopicFeaturedLink = z.infer<typeof TimelineTopicFeaturedLinkSchema>;
export type TimelineTopicFeaturedLinkComment = z.infer<typeof TimelineTopicFeaturedLinkCommentSchema>;

export type UITimelineAnalysis = z.infer<typeof UITimelineAnalysisSchema>;
